import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../../Components/base/sidebar';
import ActionBar from '../../../Components/Button/ActionBar';
import SEOForm from '../../../Components/Forms/SEOForm';
import StoryDetails from '../../../Components/Forms/StoryDetails';
import Delete from '../../../Components/Button/Delete';
import CreateBuyLinkForm from '../../../Components/Forms/CreateBuyLinkForm';
import { toast } from "sonner";

interface Item {
    ticket_id: number;
    ticket_group_code: string;
    // Add more properties as per your item structure
}

export default function TicketPricesEditor() {
    const navigate = useNavigate();
    const location = useLocation();
    const id = location.state && location.state.id;
    const [button_name, setButtonName] = useState<string[][]>([['']]);
    const [url, setUrl] = useState<string[][]>([['']]);

    const [ticket_group_code, setTicketGroupCode] = useState('');
    const [items, setItems] = useState<Item[]>([]);
    const [item, setItem] = useState<any | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [postID, setPostID] = useState<string | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const isMounted = useRef(true);
    // React state
    const [heading, setHeading] = useState<string[]>(['Ticket Selling 1']);
    const [title, setTitle] = useState<string[]>(['']);
    const [start_date, setStartDate] = useState<string[]>(['']);
    const [start_time, setStartTime] = useState<string[]>(['']);
    const [end_date, setEndDate] = useState<string[]>(['']);
    const [end_time, setEndTime] = useState<string[]>(['']);
    const [description, setDescription] = useState<string[]>(['']);
    const [is_exclusive, setIsExclusive] = useState<boolean[]>([false]);

    useEffect(() => {
        const id = location.state && location.state.id;
    }, []);

    useEffect(() => {
        isMounted.current = true;
        const fetchData = async () => {
            try {
                const response = await axios.get('https://stagingbackend.pulp.ph/api/v1/pulpliveworld/ticket', 
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                    }
                });
                if (isMounted.current) {
                    setItems(response.data.ticket);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleTicketGroupCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTicketGroupCode(e.target.value);
    };

    const handleButtonNameChange = (e: React.ChangeEvent<HTMLInputElement>, ticketIndex: number, btnIndex: number) => {
        const updated = [...button_name];
        updated[ticketIndex][btnIndex] = e.target.value;
        setButtonName(updated);
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>, ticketIndex: number, btnIndex: number) => {
        const updated = [...url];
        updated[ticketIndex][btnIndex] = e.target.value;
        setUrl(updated);
    };

    const handleAddMore = (ticketIndex: number) => {
        const updatedNames = [...button_name];
        const updatedUrls = [...url];
        updatedNames[ticketIndex].push('');
        updatedUrls[ticketIndex].push('');
        setButtonName(updatedNames);
        setUrl(updatedUrls);
    };

    const handleRemove = (ticketIndex: number, btnIndex: number) => {
        const updatedNames = [...button_name];
        const updatedUrls = [...url];
        updatedNames[ticketIndex].splice(btnIndex, 1);
        updatedUrls[ticketIndex].splice(btnIndex, 1);
        setButtonName(updatedNames);
        setUrl(updatedUrls);
    };

    // New Elements
    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newTitle = [...(title || [])];
        newTitle[index] = e.target.value;
        setTitle(newTitle);
    };

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newStartDate = [...(start_date || [])];
        newStartDate[index] = e.target.value;
        setStartDate(newStartDate);
    };

    const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newStartTime = [...(start_time || [])];
        newStartTime[index] = e.target.value;
        setStartTime(newStartTime);
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newEndDate = [...(end_date || [])];
        newEndDate[index] = e.target.value;
        setEndDate(newEndDate);
    };

    const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newEndTime = [...(end_time || [])];
        newEndTime[index] = e.target.value;
        setEndTime(newEndTime);
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newDescription = [...(description || [])];
        newDescription[index] = e.target.value;
        setDescription(newDescription);
    };

    const handleExclusiveChange = (index: number) => {
        const updated = [...is_exclusive];
        updated[index] = !updated[index];
        setIsExclusive(updated);
    };

    const handleAddNewTicket = () => {
        const newIndex = heading.length + 1;
        setHeading([...heading, `Ticket Selling ${newIndex}`]);
        setTitle([...title, '']);
        setStartDate([...start_date, '']);
        setStartTime([...start_time, '']);
        setEndDate([...end_date, '']);
        setEndTime([...end_time, '']);
        setDescription([...description, '']);
        setButtonName([...button_name, ['']]);
        setUrl([...url, ['']]);
        setIsExclusive([...is_exclusive, false]);
    };

    const handleRemoveTicket = (index: number) => {
        setHeading(heading.filter((_, i) => i !== index));
        setTitle(title.filter((_, i) => i !== index));
        setStartDate(start_date.filter((_, i) => i !== index));
        setStartTime(start_time.filter((_, i) => i !== index));
        setEndDate(end_date.filter((_, i) => i !== index));
        setEndTime(end_time.filter((_, i) => i !== index));
        setDescription(description.filter((_, i) => i !== index));
        setButtonName(button_name.filter((_, i) => i !== index));
        setUrl(url.filter((_, i) => i !== index));
        setIsExclusive(is_exclusive.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!ticket_group_code) {
            toast.error("Please select a Ticket Group Code.");
            return;
        }

        const formData = new FormData();
        formData.append('ticket_group_code', ticket_group_code);
        button_name.forEach((btnArray, ticketIndex) => {
            btnArray.forEach((btn, btnIndex) => {
                formData.append(`button_name[${ticketIndex}][]`, btn);
            });
        });

        url.forEach((urlArray, ticketIndex) => {
            urlArray.forEach((link, linkIndex) => {
                formData.append(`url[${ticketIndex}][]`, link);
            });
        });
        title.forEach((value) => formData.append('title[]', value));
        start_date.forEach((value) => formData.append('start_date[]', value));
        start_time.forEach((value) => formData.append('start_time[]', value));
        end_date.forEach((value) => formData.append('end_date[]', value));
        end_time.forEach((value) => formData.append('end_time[]', value));
        description.forEach((value) => formData.append('description[]', value));
        is_exclusive.forEach((value) => {
            formData.append('is_exclusive[]', value ? '1' : '0');
        });

        const combinedTickets = title.map((_, index) => ({
            title: title[index] || '',
            start_date: start_date[index] || '',
            start_time: start_time[index] || '',
            end_date: end_date[index] || '',
            end_time: end_time[index] || '',
            description: description[index] || '',
            button_name: button_name[index] || '',
            url: url[index] || '',
            is_exclusive: is_exclusive[index] || false
        }));

        console.log("My data:", combinedTickets);

        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) {
                console.error('Authentication token not found!');

                toast.error(<p className="text-[16px]">Authentication token not found!</p>, {
                    duration: 1500,
                });

                setIsSubmitting(false);
                return;
            }

            if (postID) {
                await axios.post(`https://stagingbackend.pulp.ph/api/v1/pulpliveworld/buy-link/${postID}`, formData, 
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${authToken}`,
                    }
                });
            } else {
                const response = await axios.post(`https://stagingbackend.pulp.ph/api/v1/pulpliveworld/buy-link`, formData, 
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${authToken}`,
                    }
                });
                setPostID(response.data.id);
            }

            toast.success(<p className="text-[16px]">Content submitted successfully.</p>, {
                duration: 1500,
            });

            setTimeout(() => {
                navigate('/view/post/buy-links');
            }, 1600);
        } catch (error) {
            console.error("An error occurred during form submission!", error);
            
            toast.error(<p className="text-[16px]">An error occurred during form submission!</p>, {
                duration: 1500,
            });
        } finally {
            setTimeout(() => {
                setIsSubmitting(false);
            }, 3000);
        }
    };

    // const handleSaveDraft = async () => {
    //     setIsSubmitting(true);

    //     const formData = new FormData();
    //     formData.append('category', category);
    //     formData.append('sub_category', sub_category);
    //     formData.append('title', title);
    //     formData.append('body', body);
    //     formData.append('seo_title', seo_title);
    //     formData.append('description', description);
    //     formData.append('sub_head', sub_head);
    //     formData.append('photo_by', photo_by);
    //     if (image) {
    //         formData.append('image', image);
    //     }
    //     if (imageUrl) {
    //         formData.append('imageUrl', imageUrl);
    //     }

    //     try {
    //         const authToken = localStorage.getItem('authToken');
    //         if (!authToken) {
    //             alert('Authentication token not found!');
    //             setIsSubmitting(false);
    //             return;
    //         }

    //         if (postID) {
    //             await axios.post(`https://stagingbackend.pulp.ph/api/v1/community/save/${postID}`, formData, {
    //                 headers: {
    //                     'Content-Type': 'multipart/form-data',
    //                     Authorization: `Bearer ${authToken}`,
    //                 }
    //             });
    //         } else {
    //             const response = await axios.post(`https://stagingbackend.pulp.ph/api/v1/community`, formData, {
    //                 headers: {
    //                     'Content-Type': 'multipart/form-data',
    //                     Authorization: `Bearer ${authToken}`,
    //                 }
    //             });
    //             setPostID(response.data.id);
    //         }
    //         alert('Draft Saved Successfully !!');
    //     } catch (error) {
    //         console.error('There was an error saving the draft!', error);
    //         alert(`There was an error saving the draft!, ${error}`);
    //     } finally {
    //         setIsSubmitting(false);
    //     }
    // };

    const handleViewShortstory = async () => {
        try {
            if (postID) {
                window.open(`https://pulpmagazine.com/article/community/${postID}`, '_blank');
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const storedRole = localStorage.getItem('role');
        setRole(storedRole);
    }, []);

    const handleDelete = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) {
                console.error('Authentication token not found!');

                toast.error(<p className="text-[16px]">Authentication token not found!</p>, {
                    duration: 1500,
                });

                return;
            }

            const confirmDelete = window.confirm('Are you sure you want to delete this content?');
            if (!confirmDelete) {
                return;
            };

            if (id) {
                await axios.delete(`https://stagingbackend.pulp.ph/api/v1/pulpliveworld/buy-link/${id}`, 
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                    }
                })
            };

            toast.success(<p className="text-[16px]">Content deleted successfully!</p>, {
                duration: 1500,
            });

            setTimeout(() => {
                navigate('/view/post/buy-links')
            }, 1600);
        } catch (error) {
            console.error('Unable to delete. Please try again.', error);

            toast.error(<p className="text-[16px]">Unable to delete. Please try again.</p>, {
                duration: 1500,
            });
        }
    };

    return (
        <>
            <div className="w-full p-5">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="GraphikSemibold text-[20px] font-semibold">Create Buy Link</h1>
                    <ActionBar
                        role={role}
                        onSubmit={handleSubmit}
                        onView={handleViewShortstory}
                        isSubmitting={isSubmitting}
                        isSaved={isSubmitting}
                    />
                </div>

                <div className="py-5 px-4 rounded bg-white">
                    <CreateBuyLinkForm
                        ticket_group_code={ticket_group_code}
                        items={items}
                        button_name={button_name}
                        url={url}
                        onTicketGroupCodeChange={handleTicketGroupCodeChange}
                        onButtonNameChange={handleButtonNameChange}
                        onUrlChange={handleUrlChange}
                        onAddMore={handleAddMore}
                        onDelete={handleRemove}
                        onAddNewTicket={handleAddNewTicket}
                        onRemoveTicket={handleRemoveTicket}
                        heading={heading}
                        title={title}
                        start_date={start_date}
                        start_time={start_time}
                        end_date={end_date}
                        end_time={end_time}
                        description={description}
                        onTitleChange={handleTitleChange}
                        onStartDateChange={handleStartDateChange} 
                        onStartTimeChange={handleStartTimeChange}
                        onEndDateChange={handleEndDateChange} 
                        onEndTimeChange={handleEndTimeChange}
                        onDescriptionChange={handleDescriptionChange}   
                        is_exclusive={is_exclusive}
                        onExclusiveChange={handleExclusiveChange}  
                    />
                    
                    {/* onClick={() => handleDelete(item.id)} */}
                </div>
            </div>
        </>
    );
}
